import { type Dispatch, type ListenerMiddleware } from "@reduxjs/toolkit";

import type { NodeAction as StateAction } from "../../../schemas/index.ts";
import type { NodeDesignsState } from "../../types.ts";
import { type NautilusStateAction } from "../nautilusActions.ts";
import type { NautilusDesignerState } from "../nautilusState.ts";
import { isNodeAction } from "./shared.ts";

type OnAction = (
  action: StateAction,
  context: { getState: () => NodeDesignsState; dispatch: (action: StateAction) => void },
) => void;

export interface NodeDesignsActionInterceptor {
  beforeAction?: OnAction;
  afterAction?: OnAction;
}

export interface NodeDesignsActionInterceptorMiddlewareOptions {
  interceptors: NodeDesignsActionInterceptor[];
}

export const nodeDesignsActionInterceptorMiddlewareCreate = ({
  interceptors,
}: NodeDesignsActionInterceptorMiddlewareOptions) => {
  const beforeInterceptors = interceptors.filter((i): i is { beforeAction: OnAction } => !!i.beforeAction);
  const afterInterceptors = interceptors.filter((i): i is { afterAction: OnAction } => !!i.afterAction);

  const middleware: ListenerMiddleware<NautilusDesignerState, Dispatch<NautilusStateAction>> = (store) => {
    const nodeDesignsStore = {
      getState: () => store.getState().nodeDesigns,
      dispatch: (action: StateAction) => {
        if (!isNodeAction(action)) {
          throw new Error("Action dispatched in node designs action interceptor must be a node designs action");
        }
        store.dispatch(action);
      },
    };

    return (next) => (action) => {
      if (!isNodeAction(action)) {
        return next(action);
      }

      for (const interceptor of beforeInterceptors) {
        interceptor.beforeAction(action, nodeDesignsStore);
      }

      const result = next(action);

      for (const interceptor of afterInterceptors) {
        interceptor.afterAction(action, nodeDesignsStore);
      }

      return result;
    };
  };
  return middleware;
};
